import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { useRouter } from '@popety-io/ui'
import { buildingPermitsService } from '../services/buildingPermitsService'
import type {
  BuildingPermit,
  BuildingPermitsData,
  PermitFilters,
  PermitSortOptions,
  BuildingPermitsContextState,
  BuildingPermitsStats,
} from '@popety-io/types'

interface BuildingPermitsContextValue extends BuildingPermitsContextState {
  fetchPermits: (propertyId: string) => Promise<void>
  loadMore: () => Promise<void>
  selectPermit: (permit: BuildingPermit | null) => void
  updateFilters: (filters: PermitFilters) => void
  updateSort: (sort: PermitSortOptions) => void
  setViewMode: (mode: 'list' | 'detail') => void
  exportPermits: (format: 'csv' | 'excel' | 'pdf') => Promise<void>
  retryFetch: () => void
  stats: BuildingPermitsStats | null
}

const BuildingPermitsContext = createContext<
  BuildingPermitsContextValue | undefined
>(undefined)

export const useBuildingPermits = () => {
  const context = useContext(BuildingPermitsContext)
  if (!context) {
    throw new Error(
      'useBuildingPermits must be used within BuildingPermitsProvider'
    )
  }
  return context
}

interface BuildingPermitsProviderProps {
  children: React.ReactNode
  propertyId?: string
  initialFilters?: PermitFilters
  propertyCoordinates?: { lat: number; lon: number }
}

export const BuildingPermitsProvider: React.FC<
  BuildingPermitsProviderProps
> = ({
  children,
  propertyId = '752',
  initialFilters = {
    radius: 500,
    categories: [],
    statuses: [],
  },
  propertyCoordinates,
}) => {
  const { query, updateQuery } = useRouter()
  const [state, setState] = useState<BuildingPermitsContextState>({
    data: null,
    loading: false,
    error: null,
    selectedPermit: null,
    filters: initialFilters,
    sort: { field: 'date', direction: 'desc' },
    viewMode: 'list',
  })

  const [stats, setStats] = useState<BuildingPermitsStats | null>(null)
  const [currentPage, setCurrentPage] = useState(1)

  const fetchPermits = useCallback(
    async (propId: string) => {
      setState((prev) => ({ ...prev, loading: true, error: null }))

      try {
        // Use the real API
        const [data] = await Promise.all([
          buildingPermitsService.searchPermits(
            propId,
            state.filters,
            state.sort,
            1,
            20,
            propertyCoordinates
          ),
        ])

        setState((prev) => ({
          ...prev,
          data,
          loading: false,
        }))

        setCurrentPage(1)
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to fetch building permits',
        }))
      }
    },
    [state.filters, state.sort]
  )

  const loadMore = useCallback(async () => {
    if (!state.data || state.loading) return

    setState((prev) => ({ ...prev, loading: true }))

    try {
      // In production, fetch next page
      // const nextPage = currentPage + 1
      // const newData = await buildingPermitsService.searchPermits(
      //   propertyId,
      //   state.filters,
      //   state.sort,
      //   nextPage,
      //   20
      // )

      // For mock data, just simulate loading
      setTimeout(() => {
        setState((prev) => ({ ...prev, loading: false }))
      }, 500)
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to load more permits',
      }))
    }
  }, [state.data, state.loading, currentPage])

  const selectPermit = useCallback((permit: BuildingPermit | null) => {
    setState((prev) => ({
      ...prev,
      selectedPermit: permit,
      viewMode: permit ? 'detail' : 'list',
    }))
  }, [])

  const updateFilters = useCallback((filters: PermitFilters) => {
    setState((prev) => ({ ...prev, filters }))
  }, [])

  const updateSort = useCallback((sort: PermitSortOptions) => {
    setState((prev) => ({ ...prev, sort }))
  }, [])

  const setViewMode = useCallback((mode: 'list' | 'detail') => {
    setState((prev) => ({ ...prev, viewMode: mode }))
  }, [])

  const exportPermits = useCallback(
    async (format: 'csv' | 'excel' | 'pdf') => {
      try {
        const blob = await buildingPermitsService.exportPermits(
          propertyId,
          state.filters,
          format
        )
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `building-permits-${propertyId}-${new Date().toISOString().split('T')[0]}.${
          format === 'excel' ? 'xlsx' : format
        }`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error exporting permits:', error)
      }
    },
    [propertyId, state.filters]
  )

  const retryFetch = useCallback(() => {
    fetchPermits(propertyId)
  }, [fetchPermits, propertyId])

  // Initial fetch
  useEffect(() => {
    fetchPermits(propertyId)
  }, []) // Only on mount

  // Refetch when filters or sort change
  useEffect(() => {
    if (state.data) {
      fetchPermits(propertyId)
    }
  }, [state.filters, state.sort])

  // Handle permitId from URL (from history tab)
  useEffect(() => {
    const permitId = query.permitId as string | undefined

    if (permitId && state.data?.permits && !state.loading) {
      // Find the permit by ID
      const permit = state.data.permits.find((p) => p.id === permitId)

      if (permit && state.selectedPermit?.id !== permitId) {
        // Select the permit
        selectPermit(permit)

        // Clear the permitId from URL after selecting
        const newQuery = { ...query }
        delete newQuery.permitId
        updateQuery(newQuery)
      }
    }
  }, [
    query.permitId,
    state.data,
    state.loading,
    state.selectedPermit,
    selectPermit,
    query,
    updateQuery,
  ])

  const value: BuildingPermitsContextValue = {
    ...state,
    stats,
    fetchPermits,
    loadMore,
    selectPermit,
    updateFilters,
    updateSort,
    setViewMode,
    exportPermits,
    retryFetch,
  }

  return (
    <BuildingPermitsContext.Provider value={value}>
      {children}
    </BuildingPermitsContext.Provider>
  )
}

export default BuildingPermitsProvider
